import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import styled, { useTheme } from "styled-components";
import useChart from "../hooks/useChart";
import { useIntersectionObserver, useSticky } from "../hooks/useSticky";
import { selectProductByColumn, toggleShowOnlyDiffs } from "../store/chart";
import { ChooseModal } from "../components/ChooseModal";
import { MobileFeatureSection } from "../components/MobileFeatureSection";
import {
  ProductBestFor,
  ProductBody,
  ProductFooter,
  ProductHeader,
} from "../components/Product";
import {
  MobileTable,
  MobileTableBody,
  MobileTableHeader,
  MobileTableRow,
  MobileTableStickyHeader,
  MobileToggleColumn,
} from "../components/Table";
import { Text } from "../components/Text";
import { Toggle } from "../components/Toggle";
import { trackTableVisible } from "../analytics";
import { usePreview } from "../hooks/usePreview";
import { MobileShopSelectSection } from "../components/MobileShopSelectSection";

export function MobileProductTable() {
  const dispatch = useDispatch();
  const { getValue, isMobile } = useTheme();
  const { isPreview } = usePreview();
  const mobileTableRef = useRef<HTMLDivElement | null>(null);
  const mobileTableHeaderRef = useRef<HTMLDivElement | null>(null);
  const mobileTableEntry = useIntersectionObserver(mobileTableRef, {
    rootMargin: `-${getValue(108)}px`,
    threshold: [0, 1],
  });
  const mobileSticky = useSticky(mobileTableHeaderRef, {
    rootMargin: `${getValue(0)}px`,
    threshold: [0, 1],
  });
  const {
    data: {
      styling,
      backgroundColor,
      productFilters,
      modal,
      showOnlyDiffsToggle,
      showOnlyDiffs,
      showProductApplication,
      productApplicationTitle,
      showBestFor,
      bestForTitle,
      useCustomProductFilters,
      showPrice,
      showShopButton,
      showShopSelectSection,
      shopSelectTitle,
      shopSelectMobileTitleTextColor,
      shopSelectMobileTitleRowColor,
      shopSelectDescription,
      shopSelectOpenOnLoad,
      mobile: {
        chooseProducts,
        chosenProducts,
        selectedFeatureMap,
        legalCopyArray,
      },
      products,
      sections,
      legalCopy,
    },
  } = useChart();
  const selectedProductColumn1 = useMemo(() => {
    if (!chosenProducts[0]) {
      return null;
    }
    return products.find((product) => product.id === chosenProducts[0]) || null;
  }, [chosenProducts, products]);
  const selectedProductColumn2 = useMemo(() => {
    if (!chosenProducts[1]) {
      return null;
    }
    return products.find((product) => product.id === chosenProducts[1]) || null;
  }, [chosenProducts, products]);

  const {
    shouldShowShopButton,
    shouldShowPrice,
    shouldShowTag,
    shouldShowSalesMessage,
    shouldShowShopSelectSection,
  } = useMemo(() => {
    const selectedProductColumns = [
      selectedProductColumn1,
      selectedProductColumn2,
    ];
    const shouldShowShopButton =
      showShopButton &&
      selectedProductColumns.filter((product) => product?.shopUrl).length > 0;
    const shouldShowPrice =
      showPrice &&
      selectedProductColumns.filter(
        (product) => product?.price || product?.savePrice,
      ).length > 0;
    const shouldShowTag =
      selectedProductColumns.filter(
        (product) => product?.useTag && product?.tag?.text,
      ).length > 0;
    const shouldShowSalesMessage =
      selectedProductColumns.filter(
        (product) => product?.useSalesMessage && product?.salesMessage?.value,
      ).length > 0;
    const shouldShowShopSelectSection =
      showShopSelectSection &&
      selectedProductColumns.filter(
        (product) => product?.showShopSelectButton && product?.shopSelectButton,
      ).length > 0;
    return {
      shouldShowShopButton,
      shouldShowPrice,
      shouldShowTag,
      shouldShowSalesMessage,
      shouldShowShopSelectSection,
    };
  }, [
    selectedProductColumn1,
    selectedProductColumn2,
    showPrice,
    showShopButton,
    showShopSelectSection,
  ]);

  useEffect(() => {
    trackTableVisible(products);
  }, [products]);

  function handleProductSelect(productId: string, column: number) {
    dispatch(selectProductByColumn({ productId, column }));
  }

  function handleToggleShowOnlyDiffs() {
    dispatch(toggleShowOnlyDiffs({ isMobile }));
  }

  return (
    <MobileTable ref={mobileTableRef}>
      <MobileTableHeader ref={mobileTableHeaderRef}>
        <MobileTableRow>
          {productFilters.length > 4 && (
            <MobileChooseModal
              modal={modal}
              chooseProducts={chooseProducts}
              chosenProducts={chosenProducts}
              showCategories={useCustomProductFilters}
              onProductSelect={handleProductSelect}
            />
          )}
        </MobileTableRow>
      </MobileTableHeader>
      <MobileTableStickyHeader
        active={!isPreview && mobileSticky && mobileTableEntry?.isIntersecting}
        backgroundColor={backgroundColor?.hex}
      >
        {(selectedProductColumn1 || selectedProductColumn2) && (
          <MobileTableRow>
            <MobileToggleColumn>
              <Toggle
                id="show-only-diffs"
                toggle={showOnlyDiffsToggle}
                checked={showOnlyDiffs}
                onChange={handleToggleShowOnlyDiffs}
              />
            </MobileToggleColumn>
            {selectedProductColumn1 && (
              <ProductHeader
                product={selectedProductColumn1}
                styling={styling}
                hiddenTag={!shouldShowTag}
              />
            )}
            {selectedProductColumn2 && (
              <ProductHeader
                product={selectedProductColumn2}
                styling={styling}
                hiddenTag={!shouldShowTag}
              />
            )}
          </MobileTableRow>
        )}
      </MobileTableStickyHeader>
      <MobileTableHeader>
        {(selectedProductColumn1 || selectedProductColumn2) && (
          <MobileTableRow>
            <MobileToggleColumn>
              <Toggle
                id="show-only-diffs"
                toggle={showOnlyDiffsToggle}
                checked={showOnlyDiffs}
                onChange={handleToggleShowOnlyDiffs}
              />
            </MobileToggleColumn>
            {selectedProductColumn1 && (
              <ProductHeader
                product={selectedProductColumn1}
                styling={styling}
                hiddenTag={!shouldShowTag}
              />
            )}
            {selectedProductColumn2 && (
              <ProductHeader
                product={selectedProductColumn2}
                styling={styling}
                hiddenTag={!shouldShowTag}
              />
            )}
          </MobileTableRow>
        )}
        {(shouldShowPrice || shouldShowShopButton) && (
          <MobileTableRow>
            <div />
            {selectedProductColumn1 && (
              <ProductBody
                product={selectedProductColumn1}
                styling={styling}
                hiddenPrice={!shouldShowPrice}
                hiddenShopButton={!shouldShowShopButton}
                hiddenSalesMessage={!shouldShowSalesMessage}
              />
            )}
            {selectedProductColumn2 && (
              <ProductBody
                product={selectedProductColumn2}
                styling={styling}
                hiddenPrice={!shouldShowPrice}
                hiddenShopButton={!shouldShowShopButton}
                hiddenSalesMessage={!shouldShowSalesMessage}
              />
            )}
          </MobileTableRow>
        )}
        {showProductApplication && (
          <MobileTableRow divider>
            <MobileApplicationColumn>
              <MobileApplicationTitle
                font={productApplicationTitle?.font}
                textColor={productApplicationTitle?.color}
              >
                {productApplicationTitle?.value}
              </MobileApplicationTitle>
            </MobileApplicationColumn>
            {selectedProductColumn1 && (
              <ProductFooter
                product={selectedProductColumn1}
                styling={styling}
              />
            )}
            {selectedProductColumn2 && (
              <ProductFooter
                product={selectedProductColumn2}
                styling={styling}
              />
            )}
          </MobileTableRow>
        )}
        {showBestFor && (
          <MobileTableRow divider>
            <MobileApplicationColumn>
              <MobileApplicationTitle
                font={bestForTitle?.font}
                textColor={bestForTitle?.color}
              >
                {bestForTitle?.value}
              </MobileApplicationTitle>
            </MobileApplicationColumn>
            {selectedProductColumn1 && (
              <ProductBestFor
                product={selectedProductColumn1}
                styling={styling}
              />
            )}
            {selectedProductColumn2 && (
              <ProductBestFor
                product={selectedProductColumn2}
                styling={styling}
              />
            )}
          </MobileTableRow>
        )}
      </MobileTableHeader>
      <MobileTableBody>
        {sections.map((section) => (
          <MobileFeatureSection
            key={section.id}
            section={section}
            selectedProductColumn1={selectedProductColumn1}
            selectedProductColumn2={selectedProductColumn2}
            selectedFeatureMap={selectedFeatureMap}
            legalCopyArray={legalCopyArray}
            styling={styling}
            showOnlyDiffs={showOnlyDiffs}
          />
        ))}
        {shouldShowShopSelectSection &&
        shopSelectTitle &&
        shopSelectDescription ? (
          <MobileShopSelectSection
            selectedProductColumn1={selectedProductColumn1}
            selectedProductColumn2={selectedProductColumn2}
            styling={styling}
            title={shopSelectTitle}
            mobileTitleTextColor={shopSelectMobileTitleTextColor}
            mobileTitleRowColor={shopSelectMobileTitleRowColor}
            description={shopSelectDescription}
            openOnLoad={shopSelectOpenOnLoad}
          />
        ) : null}
      </MobileTableBody>
      <div>
        <MobileLegalCopyList>
          {legalCopyArray.map((legalCopy, index) => (
            <li key={legalCopy}>
              <MobileLegalCopy>
                <sup>{index + 1}</sup>
                {legalCopy}
              </MobileLegalCopy>
            </li>
          ))}
        </MobileLegalCopyList>
        <MobileLegalCopyList>
          <li>
            <MobileLegalCopy>{legalCopy}</MobileLegalCopy>
          </li>
        </MobileLegalCopyList>
      </div>
    </MobileTable>
  );
}

const MobileApplicationColumn = styled.div`
  display: flex;
  justify-content: ${({ theme }) => (theme.isMobile ? "center" : "flex-start")};
  align-items: center;
  height: 100%;
  min-height: ${({ theme }) => theme.getValue(60)}px;
`;

const MobileApplicationTitle = styled(Text)`
  &&& {
    font-size: ${({ theme }) => theme.getFontSize(16)}px;
    line-height: 1;
    margin: 0;
  }
`;

const MobileChooseModal = styled(ChooseModal)`
  grid-column: 2 / 4;
  margin-bottom: ${({ theme }) => theme.getValue(6)}px;
`;

const MobileLegalCopyList = styled.ul`
  margin: ${({ theme }) => `${theme.getValue(40)}px 0 0`};
  padding: 0;
  list-style: none;

  li + li {
    margin-top: ${({ theme }) => `${theme.getValue(8)}px`};
  }
`;

const MobileLegalCopy = styled(Text)`
  &&& {
    font-size: ${({ theme }) => theme.getFontSize(12)}px;
    line-height: 1.25;
  }
`;
